import { Button } from '@src/components/ui/button';

import useCurrentLang from '@shared/hooks/useCurrentLang';
import {
    ServiceFieldTableRowDto,
    ServiceProcessFieldTableColumnVM,
} from '@shared/types/process';

import { TableFieldEditingRowCells } from './TableFieldEditingRowCells';
import { TableFieldMobileRow } from './TableFieldMobileRow';
import { TableFieldPagination } from './TableFieldPagination';
import {
    formatTableCellDisplayValue,
    getColumnTitle,
    isColumnEditable,
    queryTableRows,
    TableRowsQueryState,
} from './tableFieldUtils';
import '@src/components/ui/table.css';

import { ShadcnButton } from '@shadcn-features/core';
import { useWindowDimensions } from '@shadcn-features/core/util/useWindowDimensions';
import {
    ArrowDown,
    ArrowUp,
    ChevronsUpDown,
    Pencil,
    Trash2,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
    rows: ServiceFieldTableRowDto[];
    columns: ServiceProcessFieldTableColumnVM[];
    disabled?: boolean;
    readOnly?: boolean;
    queryState: TableRowsQueryState;
    onQueryStateChange: (
        updater: (prev: TableRowsQueryState) => TableRowsQueryState,
    ) => void;
    onChangeRows: (rows: ServiceFieldTableRowDto[]) => void;
    requestEditRowId?: string | null;
    requestFocusFirstField?: boolean;
    onRequestEditHandled?: () => void;
};

export function TableFieldGrid({
    rows,
    columns,
    disabled,
    readOnly,
    queryState,
    onQueryStateChange,
    onChangeRows,
    requestEditRowId,
    requestFocusFirstField,
    onRequestEditHandled,
}: Props) {
    const { t } = useTranslation();
    const lang = useCurrentLang();
    const { isMobile } = useWindowDimensions();
    const Table = isMobile ? 'div' : 'table';
    const Body = isMobile ? 'div' : 'tbody';

    const [editingRowId, setEditingRowId] = useState<string | null>(null);
    const [focusFirstFieldRowId, setFocusFirstFieldRowId] = useState<
        string | null
    >(null);
    const handledEditRequestRef = useRef<string | null>(null);

    useEffect(() => {
        if (!requestEditRowId) {
            handledEditRequestRef.current = null;
            return;
        }

        if (handledEditRequestRef.current === requestEditRowId) {
            return;
        }

        handledEditRequestRef.current = requestEditRowId;
        setEditingRowId(requestEditRowId);

        if (requestFocusFirstField) {
            setFocusFirstFieldRowId(requestEditRowId);
        }

        onRequestEditHandled?.();
    }, [onRequestEditHandled, requestEditRowId, requestFocusFirstField]);

    const paged = useMemo(
        () => queryTableRows(rows, columns, queryState),
        [columns, queryState, rows],
    );

    const startEdit = (row: ServiceFieldTableRowDto) => {
        setEditingRowId(row.rowId);
    };

    const cancelEdit = () => {
        setEditingRowId(null);
        setFocusFirstFieldRowId(null);
    };

    const saveEdit = (rowId: string, cells: Record<string, unknown>) => {
        onChangeRows(
            rows.map((row) =>
                row.rowId === rowId
                    ? {
                          ...row,
                          cells: { ...cells },
                      }
                    : row,
            ),
        );
        cancelEdit();
    };

    const deleteRow = (rowId: string) => {
        onChangeRows(rows.filter((row) => row.rowId !== rowId));
        if (editingRowId === rowId) {
            cancelEdit();
        }
    };

    const toggleSort = (columnId: string) => {
        onQueryStateChange((prev) => ({
            ...prev,
            page: 1,
            sortColumnId: columnId,
            sortDescending:
                prev.sortColumnId === columnId ? !prev.sortDescending : false,
        }));
    };

    const canEditRow = () => {
        if (readOnly || disabled) return false;
        return columns.some((column) => isColumnEditable(column));
    };

    const actionColSpan = readOnly ? 0 : 1;

    if (isMobile && paged.itemList.length === 0) {
        return (
            <div className='pb-4'>
                <div className='overflow-hidden rounded-lg border border-(--color-table-border-color) bg-(--color-table-cell-bg)'>
                    <div
                        aria-hidden
                        className='h-[42px] bg-(--color-table-header-bg)'
                    />
                    <p className='px-4 py-5 text-center text-sm leading-[21px] font-normal tracking-[0.07px] text-(--color-card-subtitle)'>
                        {t('translation:noData')}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className='flex w-full flex-col gap-4'>
            <div
                className={
                    isMobile
                        ? 'min-w-0'
                        : 'table-field-grid office-scrollbar min-w-0'
                }
            >
                <Table
                    className={
                        isMobile
                            ? 'w-full'
                            : 'table-field-grid__table md:w-full md:table-fixed'
                    }
                >
                    {!isMobile && (
                        <thead>
                            <tr className='table-field-grid__head-row'>
                                {columns.map((column) => {
                                    const isSorted =
                                        queryState.sortColumnId === column.id;
                                    const sortDescending =
                                        isSorted && queryState.sortDescending;

                                    return (
                                        <th
                                            key={column.id}
                                            className='table-field-grid__head-cell'
                                        >
                                            <button
                                                type='button'
                                                aria-sort={
                                                    isSorted
                                                        ? sortDescending
                                                            ? 'descending'
                                                            : 'ascending'
                                                        : 'none'
                                                }
                                                className={`table-field-grid__sort ${
                                                    isSorted
                                                        ? 'table-field-grid__sort--active'
                                                        : ''
                                                }`}
                                                onClick={() =>
                                                    toggleSort(column.id)
                                                }
                                            >
                                                {getColumnTitle(column, lang)}
                                                {isSorted ? (
                                                    sortDescending ? (
                                                        <ArrowDown
                                                            aria-hidden
                                                            className='size-4 shrink-0'
                                                        />
                                                    ) : (
                                                        <ArrowUp
                                                            aria-hidden
                                                            className='size-4 shrink-0'
                                                        />
                                                    )
                                                ) : (
                                                    <ChevronsUpDown
                                                        aria-hidden
                                                        className='size-4 shrink-0'
                                                    />
                                                )}
                                            </button>
                                        </th>
                                    );
                                })}
                                {!readOnly && (
                                    <th className='table-field-grid__head-cell w-[97px]' />
                                )}
                            </tr>
                        </thead>
                    )}
                    <Body
                        className={isMobile ? 'flex flex-col gap-5' : undefined}
                    >
                        {!isMobile && paged.itemList.length === 0 && (
                            <tr>
                                <td
                                    className='table-field-grid__empty'
                                    colSpan={columns.length + actionColSpan}
                                >
                                    {t('translation:noData')}
                                </td>
                            </tr>
                        )}

                        {paged.itemList.map((row, rowIndex) => {
                            const isEditing = editingRowId === row.rowId;
                            const rowTone = rowIndex % 2 === 1 ? 'even' : 'odd';
                            const rowNumber =
                                (queryState.page - 1) * queryState.pageSize +
                                rowIndex +
                                1;
                            const editingCells = (
                                <TableFieldEditingRowCells
                                    columns={columns}
                                    mobile={isMobile}
                                    rowNumber={rowNumber}
                                    disabled={disabled}
                                    lang={lang}
                                    row={row}
                                    focusFirstField={
                                        focusFirstFieldRowId === row.rowId
                                    }
                                    onCancel={cancelEdit}
                                    onDelete={() => deleteRow(row.rowId)}
                                    onFocusFirstFieldHandled={() =>
                                        setFocusFirstFieldRowId(null)
                                    }
                                    onSave={(cells) =>
                                        saveEdit(row.rowId, cells)
                                    }
                                />
                            );
                            if (isMobile)
                                return isEditing ? (
                                    <div key={row.rowId}>{editingCells}</div>
                                ) : (
                                    <TableFieldMobileRow
                                        key={row.rowId}
                                        row={row}
                                        columns={columns}
                                        rowNumber={rowNumber}
                                        disabled={disabled}
                                        readOnly={readOnly}
                                        editDisabled={editingRowId != null}
                                        onEdit={
                                            canEditRow()
                                                ? () => startEdit(row)
                                                : undefined
                                        }
                                        onDelete={() => deleteRow(row.rowId)}
                                    />
                                );

                            return (
                                <tr
                                    key={row.rowId}
                                    onKeyDown={(event) => {
                                        if (
                                            isEditing &&
                                            event.key === 'Escape' &&
                                            !event.defaultPrevented
                                        )
                                            cancelEdit();
                                    }}
                                    className={`table-field-grid__row table-field-grid__row--${rowTone} ${isEditing ? 'table-field-grid__row--editing' : ''}`}
                                >
                                    {isEditing ? (
                                        editingCells
                                    ) : (
                                        <>
                                            {columns.map((column) => (
                                                <td
                                                    key={column.id}
                                                    className='table-field-grid__cell'
                                                >
                                                    <span>
                                                        {formatTableCellDisplayValue(
                                                            column,
                                                            row.cells?.[
                                                                column.id
                                                            ],
                                                            lang,
                                                        ) || '—'}
                                                    </span>
                                                </td>
                                            ))}

                                            {!readOnly && (
                                                <td className='table-field-grid__cell md:px-3'>
                                                    <div className='flex items-center justify-center gap-2'>
                                                        {canEditRow() && (
                                                            <Button
                                                                className='border-border-layout size-8 shrink-0 border p-0 md:bg-(--color-body) md:text-(--color-card-subtitle)'
                                                                paddingX={0}
                                                                paddingY={0}
                                                                aria-label={t(
                                                                    'translation:edit',
                                                                )}
                                                                size='sm'
                                                                type='button'
                                                                variant='outline'
                                                                disabled={
                                                                    disabled ||
                                                                    editingRowId !=
                                                                        null
                                                                }
                                                                onClick={() =>
                                                                    startEdit(
                                                                        row,
                                                                    )
                                                                }
                                                            >
                                                                <Pencil className='size-4' />
                                                            </Button>
                                                        )}
                                                        <ShadcnButton
                                                            className='border-border-layout size-8 shrink-0 border p-0 md:bg-(--color-body)'
                                                            paddingX={0}
                                                            paddingY={0}
                                                            aria-label={t(
                                                                'translation:delete',
                                                            )}
                                                            disabled={disabled}
                                                            size='sm'
                                                            type='button'
                                                            variant='outline'
                                                            icon={
                                                                <Trash2 className='size-4 text-(--color-danger)' />
                                                            }
                                                            popconfirm={{
                                                                title: t(
                                                                    'translation:areYouSure',
                                                                ),
                                                                onConfirm: () =>
                                                                    deleteRow(
                                                                        row.rowId,
                                                                    ),
                                                            }}
                                                        />
                                                    </div>
                                                </td>
                                            )}
                                        </>
                                    )}
                                </tr>
                            );
                        })}
                    </Body>
                </Table>
            </div>

            <TableFieldPagination
                page={queryState.page}
                pageSize={queryState.pageSize}
                total={paged.itemCount}
                onPageChange={(page) =>
                    onQueryStateChange((prev) => ({ ...prev, page }))
                }
            />
        </div>
    );
}
